<template>
  <layout-header />
  <layout-sidebar />

  <div class="page-wrapper">
    <div class="content">
      <div class="row align-items-center mb-4">
        <div class="col-sm-6">
          <h6 class="fw-medium d-inline-flex align-items-center mb-3 mb-sm-0">
            <router-link :to="{ name: 'crm.companies.grid' }">
              <i class="ti ti-arrow-left me-2"></i>Companies
            </router-link>
            <span class="text-gray d-inline-flex ms-2">/ {{ company?.company_name || "—" }}</span>
          </h6>
        </div>
        <div class="col-sm-6">
          <div class="d-flex align-items-center justify-content-sm-end">
            <div class="head-icons ms-2 mb-0">
              <a href="javascript:void(0);" id="collapse-header" @click="toggleHeader">
                <i class="ti ti-chevrons-up"></i>
              </a>
            </div>
          </div>
        </div>
      </div>

      <div v-if="loading && !company" class="card border-0">
        <div class="card-body text-muted">Loading company...</div>
      </div>
      <div v-else-if="!company" class="card border-0">
        <div class="card-body text-danger">Company not found.</div>
      </div>

      <div v-else class="row">
        <div class="col-xl-4 theiaStickySidebar">
          <div class="card card-bg-1">
            <div class="card-body p-0">
              <span class="avatar avatar-xl border bg-white rounded-circle m-auto d-flex mb-2 align-items-center justify-content-center overflow-hidden">
                <img v-if="company.logo_url" :src="company.logo_url" :alt="company.company_name" class="w-100 h-100 object-fit-cover" />
                <span v-else class="fw-semibold fs-4 text-dark">{{ initials(company.company_name) }}</span>
              </span>

              <div class="text-center px-3 pb-3 border-bottom">
                <h5 class="d-flex align-items-center justify-content-center mb-1">{{ company.company_name }}</h5>
                <p class="text-dark mb-2">{{ locationLabel }}</p>
                <span class="badge" :class="statusClass(company.status)">
                  <i class="ti ti-point-filled me-1"></i>{{ company.status || "Unknown" }}
                </span>
              </div>

              <div class="p-3 border-bottom">
                <h6 class="mb-2">Company Information</h6>
                <kv-row label="Email">{{ company.email || "-" }}</kv-row>
                <kv-row label="Phone">{{ company.phone || company.phone_two || "-" }}</kv-row>
                <kv-row label="Website">
                  <a v-if="company.website_url" :href="normalizeUrl(company.website_url)" target="_blank" rel="noreferrer">
                    {{ company.website_url }}
                  </a>
                  <span v-else>-</span>
                </kv-row>
                <kv-row label="Timezone">{{ company.timezone || "-" }}</kv-row>
                <kv-row label="Created">{{ formatDate(company.created_at) }}</kv-row>
              </div>

              <div class="p-3 border-bottom">
                <h6 class="mb-2">Subscription</h6>
                <kv-row label="Plan">{{ subscription?.plan?.name || "No active plan" }}</kv-row>
                <kv-row label="Status">{{ subscription?.status || "none" }}</kv-row>
                <kv-row label="Ends At">{{ formatDate(subscription?.ends_at, "date") }}</kv-row>
                <kv-row label="Payment">{{ subscription?.payment_status || "-" }}</kv-row>
              </div>

              <div class="p-3">
                <h6 class="mb-2">Counts</h6>
                <kv-row label="Users">{{ company.users_count ?? company.users?.length ?? 0 }}</kv-row>
                <kv-row label="Employees">{{ company.employees_count ?? company.employees?.length ?? 0 }}</kv-row>
              </div>
            </div>
          </div>
        </div>

        <div class="col-xl-8">
          <div class="card border-0 mb-3">
            <div class="card-body">
              <div class="row g-3">
                <div class="col-md-4">
                  <div class="border rounded p-3 h-100">
                    <span class="text-muted d-block mb-1">Owners</span>
                    <h4 class="mb-0">{{ ownerUsers.length }}</h4>
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="border rounded p-3 h-100">
                    <span class="text-muted d-block mb-1">Employees</span>
                    <h4 class="mb-0">{{ employees.length }}</h4>
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="border rounded p-3 h-100">
                    <span class="text-muted d-block mb-1">Country</span>
                    <h4 class="mb-0">{{ company.country || company.country_code || "-" }}</h4>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <company-engagement-panel :company-id="company.id" :default-email="company.email || ''" />
        </div>
      </div>
    </div>

    <div class="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
      <p class="mb-0">2014 - {{ new Date().getFullYear() }} &copy; DoneQuick HR & Payroll</p>
      <p>Powered by <a href="#" @click.prevent class="text-primary">DoneQuick Technological World</a></p>
    </div>
  </div>
</template>

<script setup>
import { computed, defineComponent, h, onMounted, watch } from "vue";
import { useRoute } from "vue-router";
import CompanyEngagementPanel from "@/components/crm/CompanyEngagementPanel.vue";
import { useCompanyStore } from "@/stores/companyStore";

const route = useRoute();
const companyStore = useCompanyStore();

const companyId = computed(() => Number(route.params.id));
const loading = computed(() => companyStore.loading);
const company = computed(() => companyStore.company);
const subscription = computed(() => company.value?.active_subscription || null);
const employees = computed(() => company.value?.employees || []);
const ownerUsers = computed(() => (company.value?.users || []).filter((user) => Boolean(user?.pivot?.is_owner)));
const locationLabel = computed(() => [company.value?.address, company.value?.state, company.value?.country, company.value?.zip_code].filter(Boolean).join(", ") || "No address available");

async function loadCompany(id) {
  if (!Number.isFinite(id) || id <= 0) return;
  await companyStore.fetchCompany(id);
}

function toggleHeader() {
  document.getElementById("collapse-header")?.classList.toggle("active");
  document.body.classList.toggle("header-collapse");
}

function formatDate(value, mode = "datetime") {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return mode === "date" ? date.toISOString().slice(0, 10) : date.toLocaleString();
}

function initials(value = "") {
  return String(value)
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || "")
    .join("") || "CO";
}

function statusClass(status) {
  switch ((status || "").toLowerCase()) {
    case "active":
      return "badge-success";
    case "suspended":
      return "badge-warning";
    case "inactive":
      return "badge-danger";
    default:
      return "badge-secondary-transparent";
  }
}

function normalizeUrl(value) {
  if (!value) return "#";
  return /^https?:\/\//i.test(value) ? value : `https://${value}`;
}

const KvRow = defineComponent({
  name: "KvRow",
  props: { label: { type: String, default: "" } },
  setup(props, { slots }) {
    return () =>
      h("div", { class: "d-flex align-items-center justify-content-between mb-2" }, [
        h("span", { class: "d-inline-flex align-items-center" }, [h("i", { class: "ti ti-dots me-2" }), props.label]),
        h("p", { class: "text-dark mb-0 text-end" }, slots.default ? slots.default() : "-"),
      ]);
  },
});

onMounted(async () => {
  await loadCompany(companyId.value);
});

watch(companyId, async (id, previous) => {
  if (id && id !== previous) {
    await loadCompany(id);
  }
});
</script>


